import { useEffect, useState } from 'react'
import type { LevelMeta, StorageState } from '../logic/types'

interface Props {
  open: boolean
  state: StorageState
  levels: LevelMeta[]
  masteredByLevel: Record<number, number>
  onClose: () => void
  onSetSessionSize: (size: 10 | 25 | 50) => void
  onResetLevel: (level: number) => void
  onResetAll: () => void
  onToast: (msg: string) => void
}

const CONFIRM_WINDOW_MS = 2500

type Armed = 'idle' | 'confirm'
type ArmedMap = Record<number, Armed> & { __all: Armed }

function makeArmed(): ArmedMap {
  return { __all: 'idle', 1: 'idle', 2: 'idle', 3: 'idle', 4: 'idle' }
}

export function Settings({
  open,
  state,
  levels,
  masteredByLevel,
  onClose,
  onSetSessionSize,
  onResetLevel,
  onResetAll,
  onToast,
}: Props) {
  const [armed, setArmed] = useState<ArmedMap>(makeArmed)

  useEffect(() => {
    if (!open) setArmed(makeArmed())
  }, [open])

  const arm = (key: number | '__all') => {
    setArmed((prev) => ({ ...prev, [key]: 'confirm' }))
    setTimeout(() => {
      setArmed((prev) => {
        const cur = prev[key]
        if (cur === 'confirm') return { ...prev, [key]: 'idle' as Armed }
        return prev
      })
    }, CONFIRM_WINDOW_MS)
  }

  return (
    <div className={`sheet${open ? ' open' : ''}`} role="dialog" aria-label="Настройки">
      <div className="grip" />
      <div className="set-title">Настройки</div>

      <div className="set-group">
        <div className="set-label">Длина раунда</div>
        <div className="segctl" role="radiogroup" aria-label="Длина раунда">
          {([10, 25, 50] as const).map((n) => (
            <button
              key={n}
              className={state.settings.sessionSize === n ? 'on' : ''}
              onClick={() => onSetSessionSize(n)}
              role="radio"
              aria-checked={state.settings.sessionSize === n}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      <div className="set-group">
        <div className="set-label">Сбросить прогресс</div>
        {levels.map((lv) => {
          const m = masteredByLevel[lv.level] ?? 0
          const a = armed[lv.level]
          return (
            <div className="reset-row" key={lv.level}>
              <span>
                {lv.name}{' '}
                <span className="cnt">· освоено {m}</span>
              </span>
              <button
                onClick={() => {
                  if (a === 'confirm') {
                    onResetLevel(lv.level)
                    onToast(`Прогресс ${lv.name} сброшен`)
                    setArmed((p) => ({ ...p, [lv.level]: 'idle' }))
                  } else {
                    arm(lv.level)
                  }
                }}
              >
                {a === 'confirm' ? 'Точно?' : 'Сбросить'}
              </button>
            </div>
          )
        })}
      </div>

      <div className="set-group">
        <button
          className="btn danger"
          style={{ marginTop: 0 }}
          onClick={() => {
            if (armed.__all === 'confirm') {
              onResetAll()
              onToast('Весь прогресс сброшен')
              setArmed((p) => ({ ...p, __all: 'idle' }))
            } else {
              arm('__all')
            }
          }}
        >
          {armed.__all === 'confirm' ? 'Точно? Сбросить всё' : 'Сбросить всё'}
        </button>
      </div>

      <div className="set-note">
        Прогресс хранится только в этом браузере (localStorage). Никакого сервера и аккаунтов.
        Сброс — единственный способ вернуть отвеченные упражнения.
      </div>

      <div style={{ marginTop: 16 }}>
        <button className="btn ghost" style={{ width: '100%' }} onClick={onClose}>
          Sluiten
        </button>
      </div>
    </div>
  )
}
